import { createApi } from "@reduxjs/toolkit/query/react";
import { pageLimit } from "src/modules/shared/components/table/data-table";

import { createBaseServiceWithInterceptor } from "../base-service";
import { ApiRequest, ApiResponse } from "../types";
import { User } from "./types";

export const usersApi = createApi({
  reducerPath: "usersApi",
  baseQuery: createBaseServiceWithInterceptor(process.env.REACT_APP_API_URL),
  tagTypes: ["User"],
  endpoints: (builder) => ({
    getUsers: builder.query<ApiResponse<User>, ApiRequest | undefined>({
      query: (params) => ({
        url: `users`,
        params: {
          limit: pageLimit,
          page: params?.page || 1,
          sortDirection: params?.sort?.direction,
          sortField: params?.sort?.columnName,
          search: params?.search,
        },
      }),
      providesTags: (result) =>
        result?.items
          ? [
              ...result.items.map(({ id }) => ({ type: "User" as const, id })),
              { type: "User", id: "LIST" },
            ]
          : [{ type: "User", id: "LIST" }],
    }),
    editUser: builder.mutation<User, { body: FormData; id: string }>({
      query: (payload) => ({
        url: `users/${payload.id}`,
        method: "PUT",
        body: payload.body,
      }),
      invalidatesTags: (result, error, arg) => [{ type: "User", id: arg.id }],
    }),
    createUser: builder.mutation<User, FormData>({
      query: (payload) => ({
        url: "users",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: [{ type: "User", id: "LIST" }],
    }),
    deleteUser: builder.mutation<User, string>({
      query: (userId) => ({
        url: `users/${userId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) => [{ type: "User", id: arg }],
    }),
    getCurrentUser: builder.query<User, unknown>({
      query: () => ({
        url: `users/me`,
      }),
      providesTags: (result) =>
        result ? [{ type: "User" as const, id: result.id }] : ["User"],
    }),
  }),
});

export const {
  useGetUsersQuery,
  useLazyGetUsersQuery,
  useGetCurrentUserQuery,
  useEditUserMutation,
  useCreateUserMutation,
  useDeleteUserMutation,
  usePrefetch,
} = usersApi;
